import React, { useCallback } from 'react';

import { useAssistContextBanners } from 'app-components/hooks/aiChat/assistContextBanners';
import type { AssistContextBannerKey } from 'app-components/hooks/aiChat/assistContextBanners';
import ContextInfoBanner, { type ContextInfoBannerSeverity } from 'components/ContextInfoBanner';

import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import GraphicEqRoundedIcon from '@mui/icons-material/GraphicEqRounded';
import MicOffRoundedIcon from '@mui/icons-material/MicOffRounded';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import OutlinedInput from '@mui/material/OutlinedInput';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles<{ embedded: boolean }>()((theme: Theme, { embedded }) => ({
  container: {
    width: '100%',
    position: embedded ? 'relative' : 'absolute',
    bottom: embedded ? 'auto' : 0,
    left: embedded ? 'auto' : 0,
    right: embedded ? 'auto' : 0,
    padding: theme.spacing(1.5),
    paddingTop: theme.spacing(0),
  },
  paperContainer: {
    width: '100%',
    padding: theme.spacing(1),
    position: 'relative',
    zIndex: 1,
  },
  formInput: {
    padding: `${theme.spacing(0)} !important`,
    '& .MuiInputBase-input': {
      padding: `${theme.spacing(0.25)} !important`,
      ...theme.typography.paragraphRegular,
      overflow: 'auto !important' as 'auto',
      resize: 'none',
    },
    '& .MuiInputBase-multiline': {
      paddingY: theme.spacing(0.25),
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

interface ChatControlsProps {
  input: string;
  isStreaming: boolean;
  isReady: boolean;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  recording?: boolean;
  startRecording?: () => void;
  stopRecording?: () => void;
  isLoading: boolean;
  sendMessageText: (text: string, trackSend?: boolean) => void;
  embedded?: boolean;
}

type ActionButtonProps = Omit<ChatControlsProps, 'setInput' | 'sendMessageText'>;
const ASSIST_DISCLAIMER_PLACEHOLDER_URL =
  'https://support.simplifi.quicken.com/en/articles/13553075-quicken-assist-disclosure-and-consent';

const ASSIST_BANNER_WEB_IDS: Record<
  AssistContextBannerKey,
  { id: string; closeButtonId: string; severity: ContextInfoBannerSeverity }
> = {
  business: {
    id: 'ai-chat-business-subscription-banner',
    closeButtonId: 'ai-chat-business-subscription-banner-close',
    severity: 'info',
  },
  privacy: {
    id: 'ai-chat-privacy-mode-banner',
    closeButtonId: 'ai-chat-privacy-mode-banner-close',
    severity: 'warning',
  },
};

const ActionButton = ({
  input,
  recording,
  isLoading,
  isReady,
  startRecording = undefined,
  stopRecording = undefined,
}: ActionButtonProps) => {
  if (Boolean(startRecording) && Boolean(stopRecording && input.length === 0)) {
    if (recording) {
      return (
        <IconButton variant='contained' size='small' onClick={() => stopRecording && stopRecording()}>
          <MicOffRoundedIcon />
        </IconButton>
      );
    } else {
      return (
        <IconButton
          onClick={() => {
            startRecording && startRecording();
          }}
          variant='contained'
          size='small'
        >
          <GraphicEqRoundedIcon />
        </IconButton>
      );
    }
  }

  return (
    <IconButton color='primary' variant='contained' size='small' disabled={isLoading || !isReady} type='submit'>
      <ArrowUpwardRoundedIcon />
    </IconButton>
  );
};

const ChatControls = ({
  input,
  setInput,
  recording,
  startRecording,
  stopRecording,
  isStreaming,
  isLoading,
  isReady,
  sendMessageText,
  embedded = false,
}: ChatControlsProps) => {
  const theme = useTheme();
  const { banners, hasVisibleBanners } = useAssistContextBanners();
  const { classes } = useStyles({ embedded });
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const [placeholderRevealed, setPlaceholderRevealed] = React.useState(false);
  const maxRows = 6;

  React.useEffect(() => {
    const prefersReducedMotion =
      typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      setPlaceholderRevealed(true);
      return;
    }
    const id = window.requestAnimationFrame(() => {
      setPlaceholderRevealed(true);
    });
    return () => window.cancelAnimationFrame(id);
  }, []);

  const syncTextareaHeight = React.useCallback(() => {
    if (typeof window === 'undefined') return;
    const textarea = inputRef.current;
    if (!textarea || !(textarea instanceof HTMLTextAreaElement)) return;

    textarea.style.height = 'auto';
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight || '0');
    const paddingTop = parseFloat(computedStyle.paddingTop || '0');
    const paddingBottom = parseFloat(computedStyle.paddingBottom || '0');
    const borderTop = parseFloat(computedStyle.borderTopWidth || '0');
    const borderBottom = parseFloat(computedStyle.borderBottomWidth || '0');
    const maxHeight =
      lineHeight > 0 ? lineHeight * maxRows + paddingTop + paddingBottom + borderTop + borderBottom : undefined;
    const nextHeight = textarea.scrollHeight;
    const clampedHeight = maxHeight ? Math.min(nextHeight, maxHeight) : nextHeight;

    textarea.style.height = `${clampedHeight}px`;
    textarea.style.overflowY = nextHeight > clampedHeight ? 'auto' : 'hidden';
  }, [maxRows]);

  React.useEffect(() => {
    syncTextareaHeight();
  }, [input, syncTextareaHeight]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      sendMessageText(input, true);
    },
    [input, sendMessageText],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessageText(input, true);
      }
    },
    [input, sendMessageText],
  );

  return (
    <Box className={classes.container}>
      <Box sx={{ position: 'relative', width: '100%' }}>
        {hasVisibleBanners && (
          <Box sx={{ position: 'relative', zIndex: 0 }}>
            {banners.map((banner, index) => {
              const webIds = ASSIST_BANNER_WEB_IDS[banner.key];

              return (
                <ContextInfoBanner
                  key={banner.key}
                  id={webIds.id}
                  closeButtonId={webIds.closeButtonId}
                  message={banner.message}
                  severity={webIds.severity}
                  variant={index === banners.length - 1 ? 'sheet' : 'default'}
                  onDismiss={banner.onDismiss}
                />
              );
            })}
          </Box>
        )}
        <Paper
          elevation={16}
          className={classes.paperContainer}
          sx={{
            mt: hasVisibleBanners ? -1.5 : 0,
          }}
        >
          <form onSubmit={handleFormSubmit}>
            <OutlinedInput
              autoFocus
              className={classes.formInput}
              fullWidth
              size='small'
              multiline
              rows={1}
              placeholder='Ask anything...'
              value={input}
              inputComponent='textarea'
              inputRef={inputRef}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleInputKeyDown}
              sx={{
                '& .MuiInputBase-input::placeholder': {
                  opacity: placeholderRevealed ? 1 : 0,
                  transition: 'opacity 0.45s ease-out',
                  color: theme.palette.text.tertiary,
                },
              }}
            />
            <Box className={classes.footer}>
              <Typography variant='captionRegular' color='text.disabled'>
                <Link
                  href={ASSIST_DISCLAIMER_PLACEHOLDER_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  color='inherit'
                  underline='none'
                  sx={{
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline dotted',
                    },
                  }}
                >
                  Quicken AI (Beta)
                </Link>{' '}
                can make mistakes. Verify results.
              </Typography>
              <ActionButton
                input={input}
                isStreaming={isStreaming}
                isLoading={isLoading}
                isReady={isReady}
                recording={recording}
                startRecording={startRecording}
                stopRecording={stopRecording}
              />
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
};

export default ChatControls;
